/**
 * EmployeeOperationsPage — the per-employee operations lens of one Digital Employee
 * (FULL-0100 / PROD-OPSUX-014), at /agents/:id/operations.
 *
 * MASTER_FLOW §15A's MONITOR/OPERATE mental model, for one named employee rather than the fleet:
 * what this employee is doing now, what is waiting on a person, what is waiting on the customer and
 * what failed — one screen, one consistent reading, every row linking into the surface that already
 * owns the next step.
 *
 * Read-only and grants nothing. Two existing, already-permission-gated endpoints, both under the same
 * agents_view gate as the employee page: GET /api/agents/:id (the name, avatar and enablement this
 * header shows) and GET /api/agent-runs/workload-summary/:id (the buckets). The second is the
 * projection DIGEMP-AGENT-WORKLOAD-DETAIL-BACKEND-006 already shipped server-side; this page is its
 * first consumer — no new endpoint, no new authority, and no mutation anywhere on the screen. Every
 * approve, resume, retry or stop is taken on the run page a row links to, under that page's own
 * permission and idempotency rules.
 *
 * Truthfulness rules (pinned by tests):
 *  - the agent query and the workload query fail separately and say which failed — an employee whose
 *    name loaded is not presented with an empty operation as if that were a fact about the employee;
 *  - a refused (403) read is worded as a permission answer, never as a load failure;
 *  - the bucket payload is passed through whole, so an absent bucket key still reads
 *    "not reported" rather than being flattened into a zero here;
 *  - nothing on this page mutates: the rendered links are the whole interface.
 *
 * FULL-0128 / PROD-OPS-042 adds the MONITOR/OPERATE measurement half beneath the lanes: the same
 * employee's server aggregate (`GET /api/agent-runs/stats?agent_id=…`) over a declared window.
 * It is a read, it is not the page-sample `StatsRow` on the employee detail page, and it says so.
 */
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentRunsApi, agentsApi } from "../services/api";
import EmployeeAvatar from "../components/EmployeeAvatar";
import EmployeeOperationsMetrics from "../components/EmployeeOperationsMetrics";
import EmployeeOperationsPanel from "../components/EmployeeOperationsPanel";
import { OPERATIONS_METRICS_WINDOW_DAYS } from "../components/employeeOperationsMetricsReading";

/** The default look-back for the recent-failure bucket, disclosed as the endpoint's own value. */
export const OPERATIONS_STALE_MS = 30_000;

export default function EmployeeOperationsPage() {
  const { id } = useParams();

  const { data: agent, isLoading: agentLoading, error: agentError } = useQuery({
    queryKey: ["agent", id],
    queryFn: () => agentsApi.get(id),
    enabled: !!id,
  });

  const {
    data: workload,
    isLoading: workloadLoading,
    error: workloadError,
  } = useQuery({
    queryKey: ["agent-workload-detail", id],
    queryFn: () => agentRunsApi.workloadDetail(id),
    enabled: !!id,
    staleTime: OPERATIONS_STALE_MS,
    // The screen is about live state; re-reading on focus is the point, and it is a read.
    refetchOnWindowFocus: true,
  });

  // The server aggregate over the fixed, disclosed window. A separate query so a failed or refused
  // metrics read cannot take the live lanes with it, or be drawn as a zero.
  const {
    data: metrics,
    isLoading: metricsLoading,
    error: metricsError,
  } = useQuery({
    queryKey: ["agentRunStats", "agent-operations", id, OPERATIONS_METRICS_WINDOW_DAYS],
    queryFn: () =>
      agentRunsApi.stats({
        agent_id: id,
        since_days: OPERATIONS_METRICS_WINDOW_DAYS,
      }),
    enabled: !!id,
    staleTime: OPERATIONS_STALE_MS,
    refetchOnWindowFocus: true,
  });

  if (agentLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (!agent) {
    const refused = agentError?.response?.status === 403;
    return (
      <div className="text-center py-16" dir="rtl">
        <p className="text-red-600 text-sm">
          {refused ? "אין הרשאה לצפות בעובד זה" : "לא ניתן לטעון את העובד"}
        </p>
        <Link to="/agents" className="text-xs text-brand-600 hover:underline mt-2 block">
          ← חזרה לסוכנים
        </Link>
      </div>
    );
  }

  const workloadRefused = workloadError?.response?.status === 403;
  const metricsRefused = metricsError?.response?.status === 403;
  const metricsFailed = !!metricsError && !metricsRefused;

  return (
    <div className="space-y-5 max-w-5xl mx-auto" dir="rtl">
      <div>
        <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
          <Link to="/agents" className="hover:text-gray-600">סוכנים</Link>
          <span>/</span>
          <Link to={`/agents/${id}`} className="hover:text-gray-600">{agent.name}</Link>
          <span>/</span>
          <span className="text-gray-600">תפעול העובד</span>
        </div>
        <div className="flex items-center gap-3">
          <EmployeeAvatar
            name={agent.name}
            avatarKey={agent.avatar_key}
            avatarColor={agent.avatar_color}
            size="lg"
          />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">🛠️ תפעול העובד — {agent.name}</h1>
            <p className="text-sm text-gray-500 mt-0.5" data-testid="operations-purpose">
              מה העובד עושה כעת, מה ממתין להחלטה אנושית, מה ממתין ללקוח ומה נכשל — ממקור הזיהוי
              הקיים של הריצות בלבד. המסך אינו מאשר, אינו מחדש, אינו מריץ ואינו משנה דבר: כל החלטה
              מתבצעת בעמוד הריצה שאליו כל שורה מקשרת.
            </p>
          </div>
        </div>
      </div>

      {!agent.is_enabled && (
        <p
          role="status"
          data-testid="operations-disabled-notice"
          className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-2 py-1"
        >
          העובד מושבת. ריצות קיימות ממשיכות להיות מוצגות כלשונן, אך לא יתווספו חדשות.
        </p>
      )}

      {workloadRefused ? (
        <p
          role="alert"
          data-testid="operations-refused"
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
        >
          אין הרשאה לצפות בנתוני התפעול של עובד זה
        </p>
      ) : workloadError ? (
        <p
          role="alert"
          data-testid="operations-failed"
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
        >
          לא ניתן לטעון את נתוני התפעול של העובד — ייתכן שחלון הכשלים או הסטטוסים השתנו מאז הפריסה.
          אין כאן הצגה חלקית של מצב לא ידוע.
        </p>
      ) : workloadLoading || !workload ? (
        <div className="flex justify-center py-12">
          <div
            role="status"
            data-testid="operations-loading"
            className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"
          />
        </div>
      ) : (
        <EmployeeOperationsPanel payload={workload} />
      )}

      <EmployeeOperationsMetrics
        stats={metrics}
        isLoading={metricsLoading}
        isError={metricsFailed}
        isRefused={metricsRefused}
      />

      <div className="text-[11px] text-gray-400 space-y-0.5">
        <div>
          המקור: תחזית נפח העבודה הקיימת של הריצות (workload-summary) — מזהים, מצב שמור וזמנים
          בלבד. המונים מחושבים מהנתחים שהשרת מדווח עליהם; נתח שלא דווח מסומן כלא־ידוע ואינו נספר
          כאפס.
        </div>
        <div>
          מדדי התפעול מגיעים מאותו שרת (stats) על כל הריצות בחלון שמוצהר על גבי המדדים, ולא
          ממדגם הריצות שמוצג ברשימה.
        </div>
        <div>
          הרשימה מתרעננת בעת חזרה לחלון. המסך אינו כותב דבר ואינו מקנה הרשאה, כלי או אישור.
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/agents/${id}`} className="text-xs text-brand-600 hover:underline">
          ← חזרה לעמוד העובד
        </Link>
        <Link to={`/agents/${id}/cv`} className="text-xs text-brand-600 hover:underline">
          לפרופיל המקצועי ←
        </Link>
        <Link to={`/agents/${id}/skills-learning`} className="text-xs text-brand-600 hover:underline">
          למיומנויות וללמידה ←
        </Link>
      </div>
    </div>
  );
}
